import { Timer } from "lucide-react";
import type { JobTimings } from "@mycompbox/shared";

function numOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * ENG-352: the run's time breakdown from a job result (`result.timings`), or
 * null when the runner didn't send one (older runners, non-build jobs).
 */
export function extractRunTimings(result: unknown): JobTimings | null {
  if (typeof result !== "object" || result === null) return null;
  const raw = (result as Record<string, unknown>).timings;
  if (typeof raw !== "object" || raw === null) return null;
  const t = raw as Record<string, unknown>;
  const totalMs = numOrNull(t.totalMs);
  if (totalMs === null) return null;
  const fm = t.fastMode;
  let fastMode: JobTimings["fastMode"] = null;
  if (typeof fm === "object" && fm !== null) {
    const f = fm as Record<string, unknown>;
    fastMode = {
      requested: f.requested === true,
      used: f.used === true ? true : f.used === false ? false : "unknown",
    };
  }
  return {
    installMs: numOrNull(t.installMs),
    cacheHit: typeof t.cacheHit === "boolean" ? t.cacheHit : null,
    modelMs: numOrNull(t.modelMs),
    toolMs: numOrNull(t.toolMs),
    ciWaitMs: numOrNull(t.ciWaitMs),
    userWaitMs: numOrNull(t.userWaitMs),
    totalMs,
    fastMode,
  };
}

/** "1h 2m", "3m 4s", "12s", "0.4s". */
export function formatDuration(ms: number): string {
  if (ms < 1_000) return `${(ms / 1_000).toFixed(1)}s`;
  const s = Math.round(ms / 1_000);
  const h = Math.floor(s / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  const sec = s % 60;
  if (h > 0) return `${String(h)}h ${String(m)}m`;
  if (m > 0) return `${String(m)}m ${String(sec)}s`;
  return `${String(sec)}s`;
}

export interface TimingSegment {
  key: "install" | "model" | "tools" | "ci" | "user" | "other";
  label: string;
  ms: number;
  className: string;
}

/**
 * The bar's segments. CI waits run inside tool calls, so tool time excludes
 * them when both are known; "other" is the rest of the machine's wall clock
 * (setup, wrap-up, unmeasured agent time). Waiting for a reply is between
 * runs and is added on top. Zero-length segments are left out.
 */
export function timingSegments(t: JobTimings): TimingSegment[] {
  const install = t.installMs ?? 0;
  const model = t.modelMs ?? 0;
  const ci = t.ciWaitMs ?? 0;
  const tools = t.toolMs === null ? 0 : Math.max(0, t.toolMs - ci);
  const other = Math.max(0, t.totalMs - install - model - tools - ci);
  const all: TimingSegment[] = [
    { key: "install", label: "Install", ms: install, className: "bg-sky-500" },
    { key: "model", label: "Model", ms: model, className: "bg-violet-500" },
    { key: "tools", label: "Tools", ms: tools, className: "bg-emerald-500" },
    { key: "ci", label: "CI wait", ms: ci, className: "bg-amber-500" },
    { key: "user", label: "Waiting for you", ms: t.userWaitMs ?? 0, className: "bg-rose-500" },
    { key: "other", label: "Other", ms: other, className: "bg-muted-foreground/40" },
  ];
  return all.filter((s) => s.ms > 0);
}

/**
 * Parts the runner couldn't measure (null): shown as "not measured" in the
 * table, never as 0, and never drawn in the bar.
 */
export function unmeasuredParts(t: JobTimings): { key: "model" | "tools" | "ci"; label: string }[] {
  const parts: { key: "model" | "tools" | "ci"; label: string }[] = [];
  if (t.modelMs === null) parts.push({ key: "model", label: "Model" });
  if (t.toolMs === null) parts.push({ key: "tools", label: "Tools" });
  if (t.ciWaitMs === null) parts.push({ key: "ci", label: "CI wait" });
  return parts;
}

/** ENG-352: compact stacked bar + table of where a run's time went. */
export function RunTimings({ timings }: { timings: JobTimings }) {
  const segments = timingSegments(timings);
  const barTotal = segments.reduce((sum, s) => sum + s.ms, 0);
  const unmeasured = unmeasuredParts(timings);
  const modelToolUnmeasured = timings.modelMs === null || timings.toolMs === null;
  return (
    <div data-testid="run-timings">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
        <Timer className="h-4 w-4" />
        Where the time went
        <span className="font-normal text-muted-foreground">· {formatDuration(timings.totalMs)} total</span>
      </h3>
      {barTotal > 0 && (
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label="Run time breakdown">
          {segments.map((s) => (
            <div
              key={s.key}
              data-testid={`timing-segment-${s.key}`}
              className={s.className}
              style={{ width: `${String((s.ms / barTotal) * 100)}%` }}
              title={`${s.label}: ${formatDuration(s.ms)}`}
            />
          ))}
        </div>
      )}
      <table className="mt-2 text-sm">
        <tbody>
          {segments.map((s) => (
            <tr key={s.key}>
              <td className="pr-4 py-0.5">
                <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${s.className}`} />
                {s.label}
              </td>
              <td className="py-0.5 tabular-nums text-muted-foreground">{formatDuration(s.ms)}</td>
            </tr>
          ))}
          {unmeasured.map((p) => (
            <tr key={`unmeasured-${p.key}`} data-testid={`timing-unmeasured-${p.key}`}>
              <td className="pr-4 py-0.5">
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full border border-muted-foreground/40" />
                {p.label}
              </td>
              <td className="py-0.5 text-muted-foreground italic">not measured</td>
            </tr>
          ))}
          {timings.cacheHit !== null && (
            <tr>
              <td className="pr-4 py-0.5">Package cache</td>
              <td className="py-0.5 text-muted-foreground">{timings.cacheHit ? "cache hit" : "cache miss"}</td>
            </tr>
          )}
          {timings.fastMode && (
            <tr>
              <td className="pr-4 py-0.5">Fast mode</td>
              <td className="py-0.5 text-muted-foreground">
                {timings.fastMode.used === true
                  ? "on"
                  : timings.fastMode.used === false
                    ? "requested, not available"
                    : "requested"}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {modelToolUnmeasured && (
        <p className="mt-1 text-xs text-muted-foreground">
          Model/tool time is measured only for API-key runs; for this run it is part of "Other".
        </p>
      )}
    </div>
  );
}
